import { Fetcher } from '@this/src/util';
import { reportError } from '@this/lib/bugsnag';

export type PriceResultResponseArgs = {
  flight_number: string;
  changeable: boolean;
  seat_type: string;
  price: number;
  ticket_type: string;
  source: string;
  expire_at: string;
  seat_count: string | null;
  connecting_group: string | null;
};

export type FetchPricesResponseArgs = {
  success: boolean;
  prices: PriceResultResponseArgs[];
  errors?: string[];
  action_ids?: number[];
};

// ステータス確認用レスポンス型
export type ActionStatusResponseArgs = {
  id: number;
  status: string;
};

export type FetchActionStatusResponseArgs = {
  statuses: ActionStatusResponseArgs[];
  all_completed: boolean;
};

export type PriceResultArgs = {
  flightNumber: string;
  changeable: boolean;
  seatType: string;
  price: number;
  ticketType: string;
  source: string;
  expireAt: string;
  seatCount: string | null;
  connectingGroup: string | null;
};

export type FetchPricesArgs = {
  success: boolean;
  prices: PriceResultArgs[];
  errors?: string[];
  actionIds?: number[];
};

export type ActionStatusArgs = {
  id: number;
  status: string;
};

export type FetchActionStatusArgs = {
  statuses: ActionStatusArgs[];
  allCompleted: boolean;
};

export type FetchPricesSubmitArgs = {
  from_airport: string;
  to_airport: string;
  target_date: string;
  sources: string[];
  prices_only?: boolean;
  ondemand?: boolean;
  search_query_id?: number | null;
  transit_ids?: string[];
};

export const convertPriceResultResponseToArgs = (response: PriceResultResponseArgs): PriceResultArgs => ({
  flightNumber: response.flight_number,
  changeable: response.changeable,
  seatType: response.seat_type,
  price: response.price,
  ticketType: response.ticket_type,
  source: response.source,
  expireAt: response.expire_at,
  seatCount: response.seat_count,
  connectingGroup: response.connecting_group
});

export const convertFetchPricesResponseToArgs = (response: FetchPricesResponseArgs): FetchPricesArgs => ({
  success: response.success,
  prices: response.prices.map(convertPriceResultResponseToArgs),
  errors: response.errors,
  actionIds: response.action_ids
});

export const convertFetchActionStatusResponseToArgs = (
  response: FetchActionStatusResponseArgs
): FetchActionStatusArgs => ({
  statuses: response.statuses,
  allCompleted: response.all_completed
});

export const fetchDomesticAirPrices = async (params: FetchPricesSubmitArgs): Promise<FetchPricesArgs> => {
  const response = await Fetcher.post<FetchPricesResponseArgs>('/domestic_air_prices', params);
  return convertFetchPricesResponseToArgs(response);
};

export const fetchActionStatus = async (actionIds: number[]): Promise<FetchActionStatusArgs> => {
  const response = await Fetcher.get<FetchActionStatusResponseArgs>(
    `/domestic_air_fetch_actions/status?action_ids=${actionIds.join(',')}`
  );
  return convertFetchActionStatusResponseToArgs(response);
};

export const ACTION_POLLING_INTERVAL_MS = 20000;
export const ACTION_POLLING_TIMEOUT_MS = 300000;

export const waitForActionsCompleted = async (actionIds: number[]): Promise<boolean> => {
  if (actionIds.length === 0) return false;

  const startTime = Date.now();
  const poll = async (): Promise<boolean> => {
    if (Date.now() - startTime > ACTION_POLLING_TIMEOUT_MS) return false;

    try {
      const statusResponse = await fetchActionStatus(actionIds);
      if (statusResponse.allCompleted) return true;

      await new Promise<void>(resolve => {
        setTimeout(resolve, ACTION_POLLING_INTERVAL_MS);
      });
      return await poll();
    } catch (error) {
      reportError(error);
      return false;
    }
  };
  return poll();
};
